import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import {
  BanknotesIcon,
  ArrowLeftIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { formatCurrencyInput, parseCurrency, applyCurrencyMask } from '../utils/currency.ts';
import api from '../services/api.ts';

interface ReceivableFormData {
  customerName: string;
  description: string;
  originalAmount: number;
  currentAmount: number;
  interestRate: number;
  dueDate: string;
  status: 'active' | 'paid' | 'overdue' | 'renegotiated';
  category: 'real_estate' | 'vehicle' | 'mutuos' | 'other';
  paymentMethod?: string;
  installments?: number;
  currentInstallment?: number;
}

const CreateReceivable: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<ReceivableFormData>({
    customerName: '',
    description: '',
    originalAmount: 0,
    currentAmount: 0,
    interestRate: 0,
    dueDate: new Date().toISOString().split('T')[0],
    status: 'active',
    category: 'other',
    paymentMethod: '',
    installments: 1,
    currentInstallment: 1,
  });

  const [originalAmountDisplay, setOriginalAmountDisplay] = useState('');
  const [currentAmountDisplay, setCurrentAmountDisplay] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation(
    async (data: ReceivableFormData) => {
      const response = await api.post('/receivables', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('receivables');
        navigate('/receivables');
      },
      onError: (error: any) => {
        console.error('Erro ao criar recebível:', error);
      },
    }
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.customerName) newErrors.customerName = 'Informe o cliente';
    if (!formData.category) newErrors.category = 'Selecione a categoria';
    if (!originalAmountDisplay) newErrors.originalAmount = 'Informe o valor original';
    if (!currentAmountDisplay) newErrors.currentAmount = 'Informe o saldo inicial';
    if (!formData.dueDate) newErrors.dueDate = 'Informe o vencimento';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value } as any));
  };

  const handleCurrencyChange = (field: 'originalAmount' | 'currentAmount', rawValue: string) => {
    const masked = applyCurrencyMask(rawValue);
    if (field === 'originalAmount') {
      setOriginalAmountDisplay(masked);
    } else {
      setCurrentAmountDisplay(masked);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    const data: ReceivableFormData = {
      ...formData,
      originalAmount: parseCurrency(originalAmountDisplay || '0'),
      currentAmount: parseCurrency(currentAmountDisplay || '0'),
    };
    createMutation.mutate(data);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/receivables')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Criar Novo Recebível</h1>
          <p className="text-gray-600 mt-2">Adicione um novo recebível ao seu controle financeiro</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
              Informações Básicas
            </h3>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-2">
                  Cliente *
                </label>
                <input
                  type="text"
                  name="customerName"
                  id="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.customerName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: João Silva, Empresa XYZ"
                />
                {errors.customerName && (
                  <p className="text-red-500 text-sm mt-1">{errors.customerName}</p>
                )}
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria *
                </label>
                <select
                  name="category"
                  id="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="real_estate">Imóvel</option>
                  <option value="vehicle">Veículo</option>
                  <option value="mutuos">Mútuos</option>
                  <option value="other">Outros</option>
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Descreva o recebível (ex: Venda parcelada, Contrato de prestação de serviços, etc.)"
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>

              <div>
                <label htmlFor="originalAmount" className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Original *
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="originalAmount"
                    id="originalAmount"
                    value={originalAmountDisplay}
                    onChange={e => handleCurrencyChange('originalAmount', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.originalAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="R$ 0,00"
                  />
                </div>
                {errors.originalAmount && (
                  <p className="text-red-500 text-sm mt-1">{errors.originalAmount}</p>
                )}
              </div>

              <div>
                <label htmlFor="currentAmount" className="block text-sm font-medium text-gray-700 mb-2">
                  Saldo a pagar *
                </label>
                <div className="relative">
                  <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    name="currentAmount"
                    id="currentAmount"
                    value={currentAmountDisplay}
                    onChange={e => handleCurrencyChange('currentAmount', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.currentAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="R$ 0,00"
                  />
                </div>
                {errors.currentAmount && (
                  <p className="text-red-500 text-sm mt-1">{errors.currentAmount}</p>
                )}
              </div>

              <div>
                <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 mb-2">
                  Taxa de Juros (% a.m.)
                </label>
                <input
                  type="number"
                  name="interestRate"
                  id="interestRate"
                  value={formData.interestRate}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.interestRate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.interestRate && (
                  <p className="text-red-500 text-sm mt-1">{errors.interestRate}</p>
                )}
              </div>

              <div>
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Vencimento *
                </label>
                <input
                  type="date"
                  name="dueDate"
                  id="dueDate"
                  value={formData.dueDate}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.dueDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  name="status"
                  id="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="active">Ativo</option>
                  <option value="overdue">Em Atraso</option>
                  <option value="renegotiated">Renegociado</option>
                  <option value="paid">Pago</option>
                </select>
              </div>

              <div>
                <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-2">
                  Método de Recebimento
                </label>
                <input
                  type="text"
                  name="paymentMethod"
                  id="paymentMethod"
                  value={formData.paymentMethod || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Ex: PIX, Boleto, Transferência"
                />
              </div>

              <div>
                <label htmlFor="installments" className="block text-sm font-medium text-gray-700 mb-2">
                  Parcelas
                </label>
                <input
                  type="number"
                  name="installments"
                  id="installments"
                  min={1}
                  value={formData.installments || 1}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="currentInstallment" className="block text-sm font-medium text-gray-700 mb-2">
                  Parcela Atual
                </label>
                <input
                  type="number"
                  name="currentInstallment"
                  id="currentInstallment"
                  min={1}
                  value={formData.currentInstallment || 1}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/receivables')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center"
            >
              <BanknotesIcon className="h-4 w-4 mr-2" />
              Criar Recebível
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReceivable;